import React, { useMemo, useState } from "react";
import useSWR from "swr";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { updateItemEdit, bulkToggle, fetchBenchmarks, analyzeItem, analyzeAll, clearBenchmarks } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Search, MessageSquarePlus, Lock, CheckSquare, Square, MessageSquare, Sparkles, Loader2, TrendingUp, TrendingDown, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LineItemsTab({ data, onChange }) {
  const [q, setQ] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const scenario = data.scenario;
  const isBaseline = scenario.is_baseline;

  const { data: benchmarks, mutate: mutateBenchmarks } = useSWR("benchmarks", fetchBenchmarks, { refreshInterval: bulkRunning ? 4000 : 0 });
  const bench = benchmarks || {};
  const benchmarkedCount = Object.keys(bench).length;
  const remaining = Math.max(0, data.rows.length - benchmarkedCount);

  const grouped = useMemo(() => {
    const filtered = q.trim()
      ? data.rows.filter(r => (r.description + " " + r.item_no_display + " " + r.sub_category).toLowerCase().includes(q.toLowerCase()))
      : data.rows;
    const map = new Map();
    for (const r of filtered) {
      const secKey = r.section_no;
      if (!map.has(secKey)) map.set(secKey, { title: r.section_title, no: r.section_no, subs: new Map(), total: 0, included: 0 });
      const sec = map.get(secKey);
      const subKey = r.sub_category || "General";
      if (!sec.subs.has(subKey)) sec.subs.set(subKey, { name: subKey, items: [], total: 0 });
      sec.subs.get(subKey).items.push(r);
      sec.subs.get(subKey).total += r.current_amount;
      sec.total += r.current_amount;
      if (r.included) sec.included += 1;
    }
    return Array.from(map.values());
  }, [data.rows, q]);

  const patchItem = async (itemId, patch) => {
    try {
      await updateItemEdit(scenario.id, itemId, patch);
      onChange();
    } catch { toast.error("Update failed"); }
  };

  const doBulkToggle = async ({ section_no, sub_category }, included) => {
    try {
      const res = await bulkToggle(scenario.id, { section_no, sub_category, included });
      toast.success(`${included ? "Included" : "Excluded"} ${res.affected} item(s)`);
      onChange();
    } catch { toast.error("Bulk update failed"); }
  };

  const runAnalyzeItem = async (itemId) => {
    try {
      await analyzeItem(itemId);
      mutateBenchmarks();
      toast.success("Market rate updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.slice(0, 80) || "Analysis failed");
    }
  };

  const runBulkClear = async () => {
    if (!window.confirm(`Remove all ${benchmarkedCount} market analysis records? This cannot be undone.`)) return;
    try {
      await clearBenchmarks();
      toast.success("All market analysis cleared");
      mutateBenchmarks();
    } catch { toast.error("Failed to clear analysis"); }
  };

  const runAnalyzeAll = async () => {
    setBulkRunning(true);
    toast.info(`Analyzing ${remaining || data.rows.length} items — this takes ~1s per item.`);
    try {
      const res = await analyzeAll(false);
      toast.success(`Analyzed ${res.analyzed} items${res.failed ? ` (${res.failed} failed)` : ""}`);
      mutateBenchmarks();
    } catch (e) {
      toast.error("Bulk analysis failed");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap rounded-xl border border-border bg-white/95 shadow-sm p-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search description, item no, sub-category…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="search-items" />
        </div>
        {isBaseline && (
          <Badge variant="outline" className="border-primary/50 text-primary bg-primary/5 flex gap-1.5"><Lock className="h-3 w-3" /> Reference baseline · edits change all comparisons</Badge>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={runAnalyzeAll}
          disabled={bulkRunning || remaining === 0}
          className="border-primary/40 text-primary hover:bg-primary/5"
          data-testid="btn-analyze-all"
        >
          {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
          {bulkRunning ? "Analyzing…" : remaining === 0 ? `All ${benchmarkedCount} analyzed` : `Analyze ${remaining} (market)`}
        </Button>
        {benchmarkedCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={runBulkClear}
            className="border-red-500/40 text-red-700 hover:bg-red-50"
            data-testid="btn-clear-analysis"
            title="Remove all market analysis"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear analysis
          </Button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          <span className="num font-semibold text-foreground">{data.totals.included_count}</span> included · <span className="num font-semibold text-foreground">{data.totals.excluded_count}</span> excluded · <span className="num font-semibold text-amber-600">{data.totals.edited_count}</span> edited
        </div>
      </div>

      <Accordion type="multiple" defaultValue={grouped.map(s => `sec-${s.no}`)} className="space-y-3">
        {grouped.map((sec) => (
          <AccordionItem key={sec.no} value={`sec-${sec.no}`} className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between pr-4">
              <AccordionTrigger className="px-5 py-4 hover:no-underline flex-1" data-testid={`section-toggle-${sec.no}`}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-primary/15 border border-primary/30 text-primary text-xs font-mono font-bold">{sec.no}</span>
                  <span className="font-heading font-bold text-base sm:text-lg text-left">{sec.title}</span>
                  <Badge variant="secondary" className="ml-2 font-mono text-[10px]">{sec.included}/{Array.from(sec.subs.values()).reduce((a, b) => a + b.items.length, 0)} inc.</Badge>
                </div>
              </AccordionTrigger>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground uppercase tracking-wider hidden md:inline">Section Total</span>
                <span className="num font-bold text-base" data-testid={`section-total-${sec.no}`}>{fmtMoney(sec.total, false)}</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => doBulkToggle({ section_no: sec.no }, true)} title="Include all" disabled={isBaseline} data-testid={`bulk-include-${sec.no}`}>
                    <CheckSquare className="h-4 w-4 text-green-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => doBulkToggle({ section_no: sec.no }, false)} title="Exclude all" disabled={isBaseline} data-testid={`bulk-exclude-${sec.no}`}>
                    <Square className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
            <AccordionContent>
              {Array.from(sec.subs.values()).map((sub) => (
                <div key={sub.name} className="border-t border-border/60">
                  <div className="flex items-center justify-between bg-muted/40 px-5 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{sub.name}</span>
                      <Badge variant="outline" className="text-[10px]">{sub.items.length}</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="num text-xs text-muted-foreground">{fmtMoney(sub.total, false)}</span>
                      <Button variant="ghost" size="sm" onClick={() => doBulkToggle({ section_no: sec.no, sub_category: sub.name === "General" ? "" : sub.name }, true)} disabled={isBaseline}>
                        <CheckSquare className="h-3.5 w-3.5 text-green-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => doBulkToggle({ section_no: sec.no, sub_category: sub.name === "General" ? "" : sub.name }, false)} disabled={isBaseline}>
                        <Square className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground bg-background/50 border-b border-border/60">
                          <th className="px-4 py-2 w-16">Inc.</th>
                          <th className="px-4 py-2 w-16">#</th>
                          <th className="px-4 py-2">Description</th>
                          <th className="px-4 py-2 w-24 text-right">Qty</th>
                          <th className="px-4 py-2 w-16">Unit</th>
                          <th className="px-4 py-2 w-64 text-right">Rate (AED) <span className="text-primary/70 normal-case">· vs Market</span></th>
                          <th className="px-4 py-2 w-36 text-right">Amount (AED)</th>
                          <th className="px-4 py-2 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sub.items.map((r) => <LineItemRow key={r.id} row={r} onPatch={patchItem} disabled={isBaseline} benchmark={bench[r.id]} onAnalyze={runAnalyzeItem} />)}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function LineItemRow({ row, onPatch, disabled, benchmark, onAnalyze }) {
  const [qty, setQty] = useState(row.current_qty);
  const [rate, setRate] = useState(row.current_rate);
  const [notes, setNotes] = useState(row.notes || "");
  const [analyzing, setAnalyzing] = useState(false);

  React.useEffect(() => { setQty(row.current_qty); setRate(row.current_rate); setNotes(row.notes || ""); }, [row.current_qty, row.current_rate, row.notes]);

  const rowClass = `boq-row ${!row.included ? "boq-row-excluded" : ""} ${row.is_edited && row.included ? "boq-row-edited" : ""} border-b border-border/40`;
  const rateChanged = row.current_rate !== row.rate;
  const qtyChanged = row.current_qty !== row.qty;

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try { await onAnalyze(row.id); } finally { setAnalyzing(false); }
  };

  // Compute LIVE delta vs typical benchmark using the CURRENT (editable) rate
  let bDelta = null;
  if (benchmark && benchmark.typical_rate > 0) {
    bDelta = ((row.current_rate - benchmark.typical_rate) / benchmark.typical_rate) * 100;
  }

  return (
    <tr className={rowClass} data-testid={`line-row-${row.id}`}>
      <td className="px-4 py-2">
        <Switch
          checked={row.included}
          onCheckedChange={(v) => onPatch(row.id, { included: v })}
          disabled={disabled}
          data-testid={`item-toggle-${row.id}`}
        />
      </td>
      <td className="px-4 py-2 num text-xs text-muted-foreground">{row.item_no_display}</td>
      <td className="px-4 py-2">
        <div className="text-sm whitespace-pre-line max-w-[560px] leading-snug">{row.description}</div>
        {row.note && <div className="text-[10px] text-amber-600 mt-1 uppercase tracking-wider">⚠ {row.note}</div>}
      </td>
      <td className="px-4 py-2">
        <Input
          type="number"
          step="0.01"
          value={qty}
          disabled={disabled}
          onChange={e => setQty(parseFloat(e.target.value) || 0)}
          onBlur={() => qty !== row.current_qty && onPatch(row.id, { qty })}
          className={`h-8 text-right num text-sm ${qtyChanged ? "border-amber-500/60" : ""}`}
          data-testid={`item-qty-${row.id}`}
        />
      </td>
      <td className="px-4 py-2 text-xs text-muted-foreground">{row.unit}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step="0.01"
            value={rate}
            disabled={disabled}
            onChange={e => setRate(parseFloat(e.target.value) || 0)}
            onBlur={() => rate !== row.current_rate && onPatch(row.id, { rate })}
            className={`h-8 text-right num text-sm ${rateChanged ? "border-amber-500/60" : ""}`}
            data-testid={`item-rate-${row.id}`}
          />
          <MarketRateCell benchmark={benchmark} delta={bDelta} analyzing={analyzing} onAnalyze={handleAnalyze} itemId={row.id} />
        </div>
      </td>
      <td className={`px-4 py-2 text-right num font-semibold text-sm ${!row.included ? "line-through text-muted-foreground" : ""}`} data-testid={`item-amount-${row.id}`}>
        {fmtMoney(row.current_amount, false)}
      </td>
      <td className="px-4 py-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`item-notes-btn-${row.id}`}>
              {row.notes ? <MessageSquare className="h-3.5 w-3.5 text-primary" /> : <MessageSquarePlus className="h-3.5 w-3.5 text-muted-foreground" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" side="left">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Note for {row.item_no_display}</div>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add a note or comment…"
              rows={4}
              disabled={disabled}
              data-testid={`item-notes-input-${row.id}`}
            />
            <div className="mt-2 flex justify-end">
              <Button size="sm" disabled={disabled} onClick={() => onPatch(row.id, { notes })} data-testid={`item-notes-save-${row.id}`}>Save Note</Button>
            </div>
          </PopoverContent>
        </Popover>
      </td>
    </tr>
  );
}


function MarketRateCell({ benchmark, delta, analyzing, onAnalyze, itemId }) {
  if (analyzing) {
    return (
      <div className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-primary/30 bg-primary/5 text-primary text-[11px] font-mono">
        <Loader2 className="h-3 w-3 animate-spin" /> Market…
      </div>
    );
  }
  if (!benchmark) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 gap-1 text-[11px] border-primary/30 text-primary hover:bg-primary/5"
        onClick={onAnalyze}
        data-testid={`item-analyze-${itemId}`}
        title="Get market rate estimate"
      >
        <Sparkles className="h-3 w-3" /> Analyze
      </Button>
    );
  }
  const abs = Math.abs(delta || 0);
  const tone = delta === null ? "muted" :
    abs < 5 ? "fair" :
    delta > 0 ? "over" : "under";
  const toneClass = {
    fair: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
    under: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
    over: "border-red-500/40 text-red-700 bg-red-50",
    muted: "border-border text-muted-foreground bg-muted/40",
  }[tone];
  const Icon = tone === "over" ? TrendingUp : tone === "under" ? TrendingDown : Minus;
  const sign = delta > 0 ? "+" : "";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11px] font-mono ${toneClass} hover:opacity-80 transition`}
          data-testid={`item-benchmark-${itemId}`}
          title="Click for market rate analysis"
        >
          <Icon className="h-3 w-3" />
          {sign}{delta?.toFixed(1) ?? "0.0"}% <span className="opacity-60">vs market</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" side="left">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Market Analysis</span>
            </div>
            <Badge variant="outline" className="text-[10px]">
              {benchmark.confidence} confidence
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <RateStat label="Aggressive" value={benchmark.low_rate} />
            <RateStat label="Typical" value={benchmark.typical_rate} highlight />
          </div>
          <div className={`text-xs rounded-md px-2.5 py-2 border ${toneClass}`}>
            Your rate <span className="num font-semibold">AED {benchmark.current_rate_at_analysis.toFixed(2)}</span> is
            {" "}
            <span className="font-semibold">{sign}{delta?.toFixed(1)}%</span> vs typical.
            {" "}
            <span className="opacity-80">Verdict:</span> <span className="font-semibold uppercase">{benchmark.verdict.replace("_", " ")}</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{benchmark.rationale}</p>
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            <span>Model: {benchmark.model}</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={onAnalyze} data-testid={`item-reanalyze-${itemId}`}>
              <Sparkles className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RateStat({ label, value, highlight }) {
  return (
    <div className={`rounded-md border px-2 py-1.5 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}>
      <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`num text-sm font-bold ${highlight ? "text-primary" : ""}`}>{value.toFixed(0)}</div>
    </div>
  );
}
